// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * RunCell — multi-select pipeline dropdown with a run button.
 * Clicking run opens a dialog to pick versions per selected pipeline.
 */
import { useState, useMemo, type FC } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Tooltip,
  TooltipTrigger,
  DarkTooltipContent,
  TooltipProvider,
} from '@/components/ui'
import { Loader2, Play, ArrowRight, ChevronDown } from 'lucide-react'

import type { PipelineStatus, PipelineOption } from '../../models'
import type { NodeApiClient } from '@/app-shell/services'
import { InlineVersionSelect } from './InlineVersionSelect'

const DIFF_PIPELINES = new Set(['version-diff'])

type CardStatus = 'idle' | 'queued' | 'running' | 'completed' | 'error'

interface PipelineRunCard {
  id: string
  label: string
  status: CardStatus
}

export interface RunCellProps {
  documentId: string
  availablePipelines: PipelineOption[]
  pipelineStatuses: PipelineStatus[]
  onRunDocument: (documentId: string, pipelineId?: string, targetVersion?: number) => void
  onRunComparison: (documentId: string, pipelineId: string, oldVersion: number, newVersion: number) => void
  running: boolean
  basePath: string | null
  apiClient: NodeApiClient
  latestVersion: number
  selectedPipelineIds: Set<string>
  onToggleSelection: (pipelineId: string) => void
  onClearSelection: () => void
  selectedVersions: Record<string, number>
  onVersionChange: (pipelineId: string, version: number) => void
}

function normalizeStatus(raw?: string | null): CardStatus {
  const lower = raw?.toLowerCase() ?? 'idle'
  if (lower === 'completed' || lower === 'running' || lower === 'queued' || lower === 'error') {
    return lower
  }
  return 'idle'
}

export const RunCell: FC<RunCellProps> = ({
  documentId,
  availablePipelines,
  pipelineStatuses,
  onRunDocument,
  onRunComparison,
  running,
  basePath,
  apiClient,
  latestVersion,
  selectedPipelineIds,
  onToggleSelection,
  onClearSelection,
  selectedVersions,
  onVersionChange,
}) => {
  const [comparisonOld, setComparisonOld] = useState<number>(Math.max(1, latestVersion - 1))
  const [comparisonNew, setComparisonNew] = useState<number>(latestVersion)
  const [dialogOpen, setDialogOpen] = useState(false)

  const statusMap = useMemo(
    () => new Map(pipelineStatuses.map(s => [s.pipelineId, s])),
    [pipelineStatuses]
  )

  const cards: PipelineRunCard[] = useMemo(() => {
    return availablePipelines
      .filter(p => !DIFF_PIPELINES.has(p.id) || latestVersion >= 2)
      .map(p => ({
        id: p.id,
        label: p.label,
        status: normalizeStatus(statusMap.get(p.id)?.status),
      }))
  }, [availablePipelines, statusMap, latestVersion])

  const getSelectedVersion = (pipelineId: string) =>
    selectedVersions[pipelineId] ?? latestVersion

  const runnableSelectedCards = cards.filter(
    c => selectedPipelineIds.has(c.id) && c.status !== 'running' && c.status !== 'queued'
  )
  const hasRunnableSelected = runnableSelectedCards.length > 0

  const selectedCount = cards.filter(c => selectedPipelineIds.has(c.id)).length

  const triggerLabel =
    selectedCount === 0
      ? 'Select agents'
      : selectedCount === 1
        ? cards.find(c => selectedPipelineIds.has(c.id))?.label ?? '1 selected'
        : `${selectedCount} selected`

  const handleConfirmRun = () => {
    for (const card of runnableSelectedCards) {
      if (DIFF_PIPELINES.has(card.id)) {
        onRunComparison(documentId, card.id, comparisonOld, comparisonNew)
      } else {
        onRunDocument(documentId, card.id, getSelectedVersion(card.id))
      }
    }
    setDialogOpen(false)
    onClearSelection()
  }

  if (cards.length === 0) {
    return <span className="text-xs text-muted-foreground">No agents</span>
  }

  return (
    <div className="flex items-center gap-2" data-testid={`cr-run-cell-${documentId}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-7 justify-between gap-2 min-w-[10rem]"
            data-testid={`cr-pipeline-dropdown-${documentId}`}
          >
            <span className="truncate text-xs">{triggerLabel}</span>
            <ChevronDown className="h-3 w-3 shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[14rem]">
          {cards.map(card => {
            const isInProgress = card.status === 'running' || card.status === 'queued'
            return (
              <DropdownMenuCheckboxItem
                key={card.id}
                checked={selectedPipelineIds.has(card.id)}
                onCheckedChange={() => onToggleSelection(card.id)}
                onSelect={(e) => e.preventDefault()}
                data-testid={`cr-pipeline-checkbox-${card.id}`}
              >
                <span className="flex items-center gap-2">
                  <span className="truncate">{card.label}</span>
                  {isInProgress && <Loader2 className="h-3 w-3 animate-spin text-blue-500" />}
                </span>
              </DropdownMenuCheckboxItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setDialogOpen(true)}
              disabled={running || !hasRunnableSelected}
              data-testid={`cr-run-pipeline-${documentId}`}
            >
              <Play className="h-4 w-4" />
              <span className="sr-only">Run selected</span>
            </Button>
          </TooltipTrigger>
          <DarkTooltipContent side="top">Run selected</DarkTooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid={`cr-run-dialog-${documentId}`}>
          <DialogHeader>
            <DialogTitle>Choose versions to run</DialogTitle>
            <DialogDescription>
              Pick the document version each selected agent should analyze.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 py-2">
            {runnableSelectedCards.map(card => {
              const isVersionDiff = DIFF_PIPELINES.has(card.id)
              return (
                <div key={card.id} className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-foreground truncate">
                    {card.label}
                  </span>
                  {isVersionDiff ? (
                    <div className="flex items-center gap-1 shrink-0">
                      <InlineVersionSelect
                        documentId={documentId}
                        latestVersion={latestVersion}
                        selectedVersion={comparisonOld}
                        basePath={basePath}
                        apiClient={apiClient}
                        onVersionChange={setComparisonOld}
                        excludeVersion={comparisonNew}
                      />
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                      <InlineVersionSelect
                        documentId={documentId}
                        latestVersion={latestVersion}
                        selectedVersion={comparisonNew}
                        basePath={basePath}
                        apiClient={apiClient}
                        onVersionChange={setComparisonNew}
                        excludeVersion={comparisonOld}
                      />
                    </div>
                  ) : (
                    <InlineVersionSelect
                      documentId={documentId}
                      latestVersion={latestVersion}
                      selectedVersion={getSelectedVersion(card.id)}
                      basePath={basePath}
                      apiClient={apiClient}
                      onVersionChange={(v) => onVersionChange(card.id, v)}
                    />
                  )}
                </div>
              )
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRun}
              disabled={running || !hasRunnableSelected}
              data-testid={`cr-run-dialog-confirm-${documentId}`}
            >
              <Play className="h-4 w-4 mr-2" />
              Run
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
